import {useQuery} from "@tanstack/react-query";
import {api} from "../../../config/api";
import secureStorage from "../../../utils/secureStorage";
import {toLocalDateStr} from "../../../utils/dateHelpers";

// Resolve a [{startDate, endDate}] range into formatted query strings.
const resolveRange = (dateRange) => {
    const range = dateRange?.[0];
    if (!range?.startDate || !range?.endDate) return null;
    return {
        startDate: toLocalDateStr(range.startDate),
        endDate: toLocalDateStr(range.endDate),
    };
};

const fetchAnalytics = (path) => async (dateRange, startTime, endTime) => {
    const resolved = resolveRange(dateRange);
    if (!resolved) return null;

    const account = await secureStorage.getItem("selectedAccount");
    if (!account?.id) return null;

    const {data} = await api.get(
        `${path}?startDate=${resolved.startDate}&endDate=${resolved.endDate}&startTime=${startTime || "00:00"}&endTime=${endTime || "23:59"}`,
        {
            headers: {
                "X-account-id": account.id,
                Accept: "application/json",
                "Content-Type": "application/json",
            },
        }
    );
    return data;
};

const fetchSummary = fetchAnalytics("/activite/summary");
const fetchPerformance = fetchAnalytics("/activite/performance_by_hour");
const fetchRetention = fetchAnalytics("/activite/customer_retention");

const buildRangeKey = (dateRange) => {
    const resolved = resolveRange(dateRange);
    return resolved ? `${resolved.startDate}_${resolved.endDate}` : "none";
};

/**
 * Activity summary KPIs + daily timeline + payment breakdown.
 * Only fetches when `enabled` is true so hidden widgets cost nothing.
 */
export const useActivitySummary = (dateRange, startTime, endTime, refreshKey = 0, enabled = true) => {
    return useQuery({
        queryKey: ["activity-summary", buildRangeKey(dateRange), startTime, endTime, refreshKey],
        queryFn: () => fetchSummary(dateRange, startTime, endTime),
        enabled: enabled && !!resolveRange(dateRange),
        keepPreviousData: true,
        staleTime: 60_000,
    });
};

/**
 * Hourly performance: peak hour, totals, by-hour, by-period and weekday heatmap.
 */
export const usePerformanceByHour = (dateRange, startTime, endTime, refreshKey = 0, enabled = true) => {
    return useQuery({
        queryKey: ["activity-performance", buildRangeKey(dateRange), startTime, endTime, refreshKey],
        queryFn: () => fetchPerformance(dateRange, startTime, endTime),
        enabled: enabled && !!resolveRange(dateRange),
        keepPreviousData: true,
        staleTime: 60_000,
    });
};

/**
 * Customer retention: active/new/returning clients, repeat rate, frequency
 * distribution, new-vs-returning split and top clients.
 */
export const useCustomerRetention = (dateRange, startTime, endTime, refreshKey = 0, enabled = true) => {
    return useQuery({
        queryKey: ["activity-retention", buildRangeKey(dateRange), startTime, endTime, refreshKey],
        queryFn: () => fetchRetention(dateRange, startTime, endTime),
        enabled: enabled && !!resolveRange(dateRange),
        keepPreviousData: true,
        staleTime: 60_000,
    });
};
